import styled from 'styled-components'
import PropTypes from 'prop-types'
import { useEffect, useRef } from 'react'
import 'ckeditor5/ckeditor5-content.css'
import '../editor/ck-content-fixes.css'

const StyledStoryContent = styled.div`
  /* Alternative clearfix approach for containing floated elements */
  &::after {
    content: '';
    display: table;
    clear: both;
  }

  /* Only apply overflow hidden when explicitly requested via className */
  &.contain-floats {
    overflow: hidden; /* Creates new block formatting context */
  }
`

// Utility function to add accessible titles to embedded media
const addAccessibleTitlesToMedia = (containerElement) => {
  if (!containerElement) return

  const iframes = containerElement.querySelectorAll(
    '.media iframe:not([aria-label])'
  )

  iframes.forEach((iframe) => {
    const src = iframe.getAttribute('src')
    if (!src) return

    let title = `Embedded media content from ${src}`
    try {
      const url = new URL(src)
      const timestamp = Date.now()
      if (url.hostname.includes('youtube')) {
        // Extract video ID for more specific title
        const videoId =
          url.pathname.split('/embed/')[1] || url.searchParams.get('v')
        title = videoId
          ? `YouTube video (${videoId.substring(0, 8)})`
          : `YouTube video (${timestamp})`
      } else if (url.hostname.includes('vimeo')) {
        const videoId = url.pathname.split('/').pop()
        title = videoId
          ? `Vimeo video (${videoId})`
          : `Vimeo video (${timestamp})`
      } else if (
        url.hostname.includes('twitter') ||
        url.hostname.includes('x.com')
      ) {
        title = `Twitter/X post (${timestamp})`
      } else {
        title = `Embedded content from ${url.hostname} (${timestamp})`
      }
    } catch (e) {
      // Use default title if URL parsing fails
      title = `Embedded media content (${Date.now()})`
    }

    // Only use aria-label, not both title and aria-label
    iframe.setAttribute('aria-label', title)
  })
}

/**
 * Component for rendering story content with proper styling for embedded media
 */
export const StoryContent = ({ content, className, ...props }) => {
  const contentRef = useRef(null)

  useEffect(() => {
    // Add accessible titles to any embedded media that doesn't have them
    addAccessibleTitlesToMedia(contentRef.current)
  }, [content])

  return (
    <StyledStoryContent
      ref={contentRef}
      className={`ck-content ${className || ''}`}
      dangerouslySetInnerHTML={{ __html: content }}
      {...props}
    />
  )
}

StoryContent.propTypes = {
  /** HTML content to render */
  content: PropTypes.string.isRequired,
  /** Additional CSS classes */
  className: PropTypes.string
}
